import { Box, Divider, SelectChangeEvent } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaDropDown, { DropDownItem } from "../../../../../components/common/BriaDropDown/BriaDropDown";
import StaticDropdown, { AspectRatio } from "../../../../../components/common/DropDowns/StaticDropdown";
import useStaticDropdown from "../../../../../components/common/DropDowns/useStaticDropdown";
import { APPS } from "../../../../../constants/AppsConstants";
import LinkConstants from "../../../../../constants/LinkConstants";
import { useAppStore } from "../../../../../hooks/useStores";
import InputLayout from "../../../../../layout/InputLayout/InputLayout";
import { ImageEditingViewMode } from "../../../../../models/image-to-image";
import { FPS } from "../../../../../models/video-editing";
import NewTgDropdown from "../../../Models/Dropdown/NewTgDropdown";
import { TgModel } from "../../../Models/store/tg-model-store";
import VideoEditingInputFrames from "./InputFrames/VideoEditingInputFrames";
import TgPromptInput from "./PromptInput/TgPromptInput";
import styles from "./TgVideoEditingConfig.module.scss";

const TgVideoEditingConfig = () => {
	const { t } = useTranslation("tg", { keyPrefix: "app.video.config" });
	const { videoEditingStore, playgroundStore } = useAppStore();
	const { items: fpsOptions } = useStaticDropdown<FPS>({ tKeyPrefix: "app.video.config.fps.options" });
	const { tgConfig, handleTgConfigChange } = videoEditingStore;
	const [selectedModel, setSelectedModel] = useState<TgModel>();
	const disablePrompt = !tgConfig.model_id;

	useEffect(() => {
		playgroundStore.handleAppChange(APPS.TG_VIDEO_EDITING);
		playgroundStore.imageEditingViewMode = ImageEditingViewMode.SINGLE;
	}, []);

	const handleModelChange = (_e: SelectChangeEvent<string>, selectedItem: DropDownItem<string, TgModel>) => {
		setSelectedModel(selectedItem.extraData);
		handleTgConfigChange("model_id", selectedItem.value);
	};

	return (
		<>
			<Box className={styles.config}>
				<Box className={styles.inputs}>
					<InputLayout label={t("models.label")} docsLink={LinkConstants.TG.TRAIN_MODEL.DOCS}>
						<NewTgDropdown
							value={videoEditingStore.tgConfig.model_id as string}
							onChange={handleModelChange}
						/>
					</InputLayout>
					<InputLayout label={t("aspectRatio.label")}>
						<StaticDropdown
							type="aspectRatio"
							value={tgConfig.aspect_ratio}
							onChange={(e) => handleTgConfigChange("aspect_ratio", e.target.value as AspectRatio)}
							disabled
						/>
					</InputLayout>
					<InputLayout label={t("fps.label")}>
						<BriaDropDown<FPS>
							items={fpsOptions.map((option) => ({ ...option, value: Number(option.value) as FPS }))}
							value={tgConfig.fps}
							onChange={(e) => handleTgConfigChange("fps", e.target.value as FPS)}
							disabled
						/>
					</InputLayout>
					<Divider className={styles.divider} />
					<InputLayout
						label={t("inputFrames.label")}
						docsLink={LinkConstants.VIDEO_EDITING.REMOVE_BACKGROUND.DOCS}
						optional
					>
						<VideoEditingInputFrames />
					</InputLayout>
					<Divider className={styles.divider} />
					<InputLayout label={t("prompt.label")}>
						<TgPromptInput
							prefix={selectedModel?.generation_prefix}
							prompt={tgConfig.prompt}
							placeholder={t("prompt.placeholder")}
							onChange={(newPrompt) => handleTgConfigChange("prompt", newPrompt)}
							onEnter={() => videoEditingStore.generateTgVideo(tgConfig)}
							disabled={disablePrompt}
						/>
					</InputLayout>
				</Box>
				<BriaButton
					className={styles.primaryBtn}
					buttonType="primaryMedium"
					loading={videoEditingStore.loadingGenerate}
				>
					{t("primaryBtn")}
				</BriaButton>
			</Box>
		</>
	);
};

export default observer(TgVideoEditingConfig);
